import { EntityId } from "@reduxjs/toolkit";

import { streamingApi } from "./base.api";

import { Dossier } from "types/Dossier";
import { CreateDossierRequest } from "types/CreateDossierRequest";
import { PaginatedResponse } from "types/PaginatedResponse";
import { PaginatedRequest } from "types/PaginatedRequest";
import { PersistedRecording } from "types/PersistedRecording";
import { PatchDossierInformationRequest } from "types/PatchDossierInformationRequest";
import { DossierCapability } from "types/DossierCapability";
import { DossierExport } from "types/DossierExport";
import { DossierExportsForDossierPaginatedRequest } from "types/DossierExportsForDossierPaginatedRequest";
import { StartDossierExportRequest } from "types/StartDossierExportRequest";
import { luxonUtils } from "utils/luxonUtils";
import { ObjectDetectionFrame } from "types/reId/ObjectDetectionFrame";
import { ObjectDetectionModel } from "types/reId/ObjectDetectionModel";
import { VectorSearchResultRecord } from "types/reId/VectorSearchResultRecord";
import { PersistentRecordingTaskStatistics } from "types/PersistentRecordingTaskStatistics";
import { ReIdentificationStatus } from "types/ReIdentificationStatus";
import { PatchReIdentificationModelSelectionDossierCapabilitySettingRequest } from "types/PatchReIdentificationModelSelectionDossierCapabilitySettingRequest";
import { ReIdentificationModelSelectionDossierCapabilitySetting } from "types/reId/ReIdentificationModelSelectionDossierCapabilitySetting";
import { ObjectDetectionModelName } from "types/ObjectDetectionModelName";

const BASE_PATH = "/case-manager";

export const caseManagerApi = streamingApi.injectEndpoints({
    endpoints: (build) => ({
        getDossiers: build.query<PaginatedResponse<Dossier>, PaginatedRequest>({
            query: (payload) => {
                const queryParams = new URLSearchParams(
                    Object.entries(payload)
                        .filter(([, value]) => !!value)
                        .map(([key, value]) => [key, String(value)]),
                );

                return `${BASE_PATH}/Dossiers?${queryParams}`;
            },
            providesTags: (result) =>
                result
                    ? [
                          // Provides a tag for each post in the current page,
                          // as well as the 'PARTIAL-LIST' tag.
                          ...result.data.map(({ id }) => ({
                              type: "DossierOverview" as const,
                              id,
                          })),
                          { type: "DossierOverview", id: "PARTIAL-LIST" },
                      ]
                    : [{ type: "DossierOverview", id: "PARTIAL-LIST" }],
        }),
        getDossiersCapabilities: build.query<DossierCapability[], undefined>({
            query: () => `${BASE_PATH}/DossierCapabilities`,
        }),
        getDossierById: build.query<Dossier, EntityId>({
            query: (dossierId) => `${BASE_PATH}/Dossiers/${dossierId}`,
            providesTags: (result, error, arg) => [
                { type: "Dossier", id: arg },
            ],
        }),
        createDossier: build.mutation<Dossier, CreateDossierRequest>({
            query: (arg) => ({
                url: `${BASE_PATH}/Dossiers`,
                method: "POST",
                body: {
                    ...arg,
                    // Convert to UTC to match backend format, change when back-end fully supports NodaTime
                    startTime: luxonUtils
                        .fromIso(arg.startTime)
                        ?.toUTC()
                        .toISO(),
                    endTime: luxonUtils.fromIso(arg.endTime)?.toUTC().toISO(),
                },
            }),
            invalidatesTags: () => [
                { type: "DossierOverview", id: "PARTIAL-LIST" },
            ],
        }),
        removeDossier: build.mutation<
            undefined,
            {
                id: EntityId;
            }
        >({
            query: (arg) => ({
                url: `${BASE_PATH}/Dossiers/${arg.id}`,
                method: "DELETE",
            }),
            invalidatesTags: (result, error, arg) => [
                { type: "DossierOverview", id: arg.id },
                { type: "DossierOverview", id: "PARTIAL-LIST" },
            ],
        }),
        patchDossierInformation: build.mutation<
            Dossier,
            PatchDossierInformationRequest
        >({
            query: (arg) => ({
                url: `${BASE_PATH}/Dossiers/${arg.id}`,
                method: "PATCH",
                body: {
                    $type: "patchDossierInformation",
                    ...arg,
                    // Convert to UTC to match backend format, change when back-end fully supports NodaTime
                    startTime: luxonUtils
                        .fromIso(arg.startTime)
                        ?.toUTC()
                        .toISO(),
                    endTime: luxonUtils.fromIso(arg.endTime)?.toUTC().toISO(),
                },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: "Dossier", id: arg.id },
                { type: "DossierOverview", id: arg.id },
                { type: "DossierOverview", id: "PARTIAL-LIST" },
            ],
        }),
        getPersistedRecordingsByDossierDeviceId: build.query<
            PersistedRecording[],
            { dossierId: EntityId; dossierDeviceId: EntityId }
        >({
            query: (payload) =>
                `${BASE_PATH}/Dossiers/${payload.dossierId}/DossierDevices/${payload.dossierDeviceId}/PersistedRecordings`,
            providesTags: (result, error, arg) => [
                { type: "Dossier", id: arg.dossierId },
            ],
        }),

        // Persisted recording tasks
        retryFailedDownload: build.mutation<
            PersistedRecording,
            {
                dossierId: EntityId;
                persistedRecordingId: EntityId;
            }
        >({
            query: (arg) => ({
                url: `${BASE_PATH}/Dossiers/${arg.dossierId}/PersistedRecordings/${arg.persistedRecordingId}/PersistedRecordingTasks/Downloads/Retry`,
                method: "PATCH",
            }),
            invalidatesTags: (result, error, arg) => [
                { type: "DossierOverview", id: arg.dossierId },
                { type: "DossierOverview", id: "PARTIAL-LIST" },
            ],
        }),
        retryAllFailedDownloads: build.mutation<
            undefined,
            {
                id: EntityId;
            }
        >({
            query: (arg) => ({
                url: `${BASE_PATH}/Dossiers/${arg.id}/PersistedRecordingTasks/Downloads/Retry/Failed`,
                method: "PATCH",
            }),
            invalidatesTags: (result, error, arg) => [
                { type: "DossierOverview", id: arg.id },
                { type: "DossierOverview", id: "PARTIAL-LIST" },
            ],
        }),
        getReIdentificationPersistedRecordingTaskStatistics: build.query<
            PersistentRecordingTaskStatistics<ReIdentificationStatus>,
            {
                dossierId: EntityId;
                objectDetectionModelName?: ObjectDetectionModelName;
            }
        >({
            query: (payload) => {
                return `${BASE_PATH}/Dossiers/${payload.dossierId}/PersistedRecordingTaskStatistics/ReIdentification?model=${payload.objectDetectionModelName}`;
            },
        }),

        // Dossier Exports
        getDossierExportsForDossier: build.query<
            PaginatedResponse<DossierExport>,
            DossierExportsForDossierPaginatedRequest
        >({
            query: (payload) => {
                const queryParams = new URLSearchParams(
                    Object.entries(payload)
                        .filter(([, value]) => !!value)
                        .map(([key, value]) => [key, String(value)]),
                );

                return `${BASE_PATH}/Dossiers/${payload.dossierId}/DossierExports?${queryParams}`;
            },
        }),
        startDossierExport: build.mutation<string, StartDossierExportRequest>({
            query: (arg) => ({
                url: `${BASE_PATH}/Dossiers/${arg.dossierId}/DossierExports`,
                method: "POST",
                body: arg,
            }),
        }),

        // DossierCapabilitySettingsController
        getReIdentificationModelSelectionDossierCapabilitySetting: build.query<
            ReIdentificationModelSelectionDossierCapabilitySetting,
            {
                dossierId: EntityId;
            }
        >({
            query: (payload) =>
                `${BASE_PATH}/Dossiers/${payload.dossierId}/DossierCapabilitySettings/ReIdentificationModelSelectionDossierCapabilitySetting`,
            providesTags: (result, error, arg) => [
                {
                    type: "ReIdentificationModelSelectionDossierCapabilitySetting",
                    id: arg.dossierId,
                },
            ],
        }),
        patchReIdentificationModelSelectionDossierCapabilitySetting:
            build.mutation<
                undefined,
                PatchReIdentificationModelSelectionDossierCapabilitySettingRequest
            >({
                query: (payload) => ({
                    url: `${BASE_PATH}/Dossiers/${payload.dossierId}/DossierCapabilitySettings`,
                    method: "PATCH",
                    body: {
                        $type: "patchReIdentificationModelSelectionDossierCapabilitySetting",
                        ...payload,
                    },
                }),
                invalidatesTags: (result, error, arg) => [
                    {
                        type: "ReIdentificationModelSelectionDossierCapabilitySetting",
                        id: arg.dossierId,
                    },
                ],
            }),

        // ReIdentification
        getAllObjectDetectionModels: build.query<
            ObjectDetectionModel[],
            undefined
        >({
            query: () => `${BASE_PATH}/ObjectDetectionModels`,
        }),
        getObjectDetectionsByStartAndStoptime: build.query<
            ObjectDetectionFrame[],
            {
                dossierId: EntityId;
                startTime: string | null;
                stopTime: string | null;
                objectDetectionModelName: ObjectDetectionModelName;
            }
        >({
            query: (payload) => {
                const startTime = luxonUtils
                    .fromIso(payload.startTime ?? "")
                    ?.toUTC()
                    .toISO();

                const stopTime = luxonUtils
                    .fromIso(payload.stopTime ?? "")
                    ?.toUTC()
                    .toISO();

                return `${BASE_PATH}/Dossiers/${payload.dossierId}/ObjectDetectionFrames?startTime=${startTime}&stopTime=${stopTime}&model=${payload.objectDetectionModelName}`;
            },
        }),
        getPersistedRecordingsByDetectedObjectIdId: build.query<
            VectorSearchResultRecord[],
            {
                dossierId: EntityId;
                persistedRecordingId: EntityId;
                trackerId: number;
                classId: number;
                maxDistance: number;
                objectDetectionModelName: ObjectDetectionModelName;
            }
        >({
            query: (payload) => {
                return `${BASE_PATH}/Dossiers/${payload.dossierId}/PersistedRecordings/${payload.persistedRecordingId}/ObjectDetectionTrackerIdentities/${payload.trackerId}/Class/${payload.classId}/Matches?maxDistance=${payload.maxDistance}&model=${payload.objectDetectionModelName}`;
            },
        }),
    }),
    overrideExisting: true,
});

export const {
    useGetDossiersQuery,
    useGetDossierByIdQuery,
    useGetDossiersCapabilitiesQuery,
    useCreateDossierMutation,
    usePatchDossierInformationMutation,
    useRemoveDossierMutation,
    useRetryFailedDownloadMutation,
    useRetryAllFailedDownloadsMutation,
    useStartDossierExportMutation,
    useGetReIdentificationModelSelectionDossierCapabilitySettingQuery,
    usePatchReIdentificationModelSelectionDossierCapabilitySettingMutation,
    useGetAllObjectDetectionModelsQuery,
    useGetPersistedRecordingsByDetectedObjectIdIdQuery,
    useGetReIdentificationPersistedRecordingTaskStatisticsQuery,
} = caseManagerApi;
